import React, { useEffect, useState } from "react";
import QRCode from "qrcode";
import { UserPlus, Video, ClipboardList, Upload, BadgeCheck, Sun, Eye, Shield, Flag, Flame } from "lucide-react";
import { ACADEMY_LOGO } from "../data/assets";
import { Page, DocHeader, Kicker, H1, SectionHead, PageStyle, GOLD, RED, GOLD_GRAD, RED_GRAD, PANEL } from "./print/academyDoc";

const HUB = "https://hub.nextosoccer.com";
const WHAT = "The Next O Technical Academy is an online training system built for serious soccer players. You train at home and at sessions, upload video proof of your work, and Coach Barker reviews your progress and gives you specific feedback. Everything you do is tracked so you can see yourself getting better.";
const STEPS = [
  [UserPlus, "Join and claim your player profile at hub.nextosoccer.com."],
  [Video, "Upload a baseline video doing toe taps and foundations to set your starting tier (Bronze, Silver, Gold, or Elite)."],
  [ClipboardList, "Every week Coach Barker assigns you specific homework drills with a rep target."],
  [Upload, "You record yourself doing the homework and upload your video proof before your next session."],
  [BadgeCheck, "Coach Barker reviews your video, tracks your BEOGO score, and unlocks your next progression."],
];
const TIERS = [
  ["Bronze", "Learning the basics, building consistency.", "#CD7F32"],
  ["Silver", "Both feet developing, speed increasing.", "#C0C0C0"],
  ["Gold", "Game-pace execution, scanning and decision-making.", GOLD],
  ["Elite", "Full game transfer, pressure and finishing at speed.", RED],
];
const FIVE_OS = [
  [Sun, "Opportunity", "Every rep is a chance to get better. Use it."],
  [Eye, "Observation", "See the field before you touch the ball."],
  [Shield, "Ownership", "Your development is your responsibility."],
  [Flag, "Optimism", "Confidence comes from doing the work."],
  [Flame, "Outwork", "The player who works hardest wins."],
];

// /print/cheat-sheet → "What Is The Next O Technical Academy?" one-page parent/player cheat sheet (8.5×11), rendered by scripts/render_doc.py
export default function PrintCheatSheet() {
  const [qr, setQr] = useState("");
  useEffect(() => { QRCode.toDataURL(HUB, { margin: 1, width: 600, errorCorrectionLevel: "H", color: { dark: "#0a0a0a", light: "#ffffff" } }).then(setQr); }, []);
  return (
    <div data-testid="cheat-sheet-doc" className="bg-black" style={{ width: "8.5in" }}>
      <PageStyle />
      <Page footer="Parent & Player Cheat Sheet" testid="cheat-sheet-page">
        <DocHeader title="Parent & Player Cheat Sheet" />
        <div className="mt-4 flex items-start justify-between gap-6">
          <div className="flex-1 min-w-0">
            <Kicker>Start here</Kicker>
            <H1 size={40}>What is The Next O<br /><span style={{ color: GOLD }}>Technical Academy?</span></H1>
          </div>
          <img src={ACADEMY_LOGO} alt="" style={{ height: 86, filter: `drop-shadow(0 0 16px ${GOLD}55)` }} />
        </div>

        <SectionHead n={1}>What It Is</SectionHead>
        <div data-testid="cheat-what" className="rounded-lg border-l-[3px] px-5 py-3.5 text-[11.5px] leading-relaxed text-white/85" style={{ borderColor: GOLD, background: `linear-gradient(120deg, ${GOLD}14, ${PANEL})` }}>{WHAT}</div>

        <SectionHead n={2} sub="Five simple steps — repeat every week">How It Works</SectionHead>
        <div data-testid="cheat-steps" className="grid grid-cols-5 gap-2">
          {STEPS.map(([Icon, t], i) => (
            <div key={i} className="relative rounded-lg border p-3 overflow-hidden flex flex-col" style={{ borderColor: i === 4 ? `${GOLD}88` : `${GOLD}44`, background: PANEL }}>
              <div className="absolute right-2 top-0 font-cover text-5xl" style={{ color: `${GOLD}1a` }}>{i + 1}</div>
              <div className="w-8 h-8 rounded-md flex items-center justify-center shrink-0" style={{ background: i === 4 ? GOLD_GRAD : RED_GRAD }}><Icon size={15} color={i === 4 ? "#000" : "#fff"} /></div>
              <div className="font-mono text-[7px] uppercase tracking-[0.3em] mt-2.5" style={{ color: GOLD }}>Step {i + 1}</div>
              <p className="text-[9.3px] text-white/80 mt-1 leading-snug">{t}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-[1.1fr_1fr] gap-5">
          <div>
            <SectionHead n={3}>Your Tier</SectionHead>
            <table data-testid="cheat-tiers" className="w-full border-collapse" style={{ fontSize: 10 }}>
              <tbody>
                {TIERS.map(([t, d, c], i) => (
                  <tr key={t} className="border-b" style={{ borderColor: "rgba(255,255,255,0.12)", height: 44, background: i % 2 ? "rgba(255,255,255,0.025)" : "transparent" }}>
                    <td className="pr-3 whitespace-nowrap"><span className="inline-flex items-center gap-2 font-headings text-[15px] uppercase tracking-wide"><span className="w-3 h-3 rounded-full" style={{ background: c, boxShadow: `0 0 8px ${c}88` }} />{t}</span></td>
                    <td className="text-[9.8px] text-white/78 leading-snug">{d}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div>
            <SectionHead n={4}>The 5 O's</SectionHead>
            <div data-testid="cheat-five-os" className="space-y-1.5">
              {FIVE_OS.map(([Icon, t, d]) => (
                <div key={t} className="flex items-center gap-3 rounded-md border px-3 py-1.5" style={{ borderColor: `${GOLD}33`, background: `linear-gradient(120deg, ${GOLD}0e, ${PANEL})` }}>
                  <span className="w-6 h-6 rounded-sm flex items-center justify-center font-cover text-black text-sm shrink-0" style={{ background: GOLD_GRAD }}>O</span>
                  <div className="min-w-0"><div className="flex items-center gap-1.5 font-headings text-[12.5px] uppercase tracking-wide leading-none"><Icon size={11} style={{ color: GOLD }} /> {t}</div><div className="text-[9.3px] text-white/75 leading-snug mt-0.5">{d}</div></div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div data-testid="cheat-footer" className="mt-auto flex items-center gap-5 rounded-lg border p-4" style={{ borderColor: `${GOLD}66`, background: `${GOLD}0d` }}>
          <div className="bg-white rounded-md p-1.5 shrink-0" data-testid="cheat-qr">{qr ? <img src={qr} alt="QR code to hub.nextosoccer.com" style={{ width: 92, height: 92, display: "block" }} /> : <div style={{ width: 92, height: 92 }} />}</div>
          <div className="flex-1 min-w-0">
            <div className="font-mono text-[8px] font-bold uppercase tracking-[0.4em]" style={{ color: GOLD }}>Scan to start</div>
            <div className="font-cover text-[26px] uppercase leading-none mt-1.5">Questions? <span style={{ color: GOLD }}>hub.nextosoccer.com</span></div>
            <div className="font-headings text-[13px] uppercase tracking-wide text-white/80 mt-2">Coach Barker <span className="text-white/35 mx-1.5">|</span> Next O Technical Academy</div>
          </div>
          <div className="font-mono text-[8px] uppercase tracking-[0.3em] text-white/45 text-right leading-loose shrink-0">Train · Develop · Compete<br />Bronze → Elite</div>
        </div>
      </Page>
    </div>
  );
}
